import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { config, logger } from '@/common/services/config';

let appInsights: ApplicationInsights | null = null;

export function initializeTelemetry() {
  try {
    // Initialisation d'Application Insights
    appInsights = new ApplicationInsights({
      config: {
        connectionString: config.applicationInsight.licenseKey,
        enableAutoRouteTracking: true, // Pour les SPAs
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        disableExceptionTracking: false,
        disableAjaxTracking: false,
        disableFetchTracking: false,
        excludeRequestFromAutoTrackingPatterns: [/\/health$/, /\/ping$/],
      },
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    // Configuration des informations utilisateur et session
    appInsights.setAuthenticatedUserContext('anonymous');

    logger.log('Application Insights initialisé avec succès');
  } catch (error) {
    logger.error("Erreur lors de l'initialisation d'Application Insights:", error);
  }
}

// Fonction pour tracker des événements personnalisés
export function trackEvent(
  name: string,
  properties?: Record<string, any>,
  measurements?: Record<string, number>
) {
  if (appInsights) {
    appInsights.trackEvent({
      name,
      properties,
      measurements,
    });
  }
}

// Fonction pour tracker des erreurs
export function trackError(
  error: Error,
  properties?: Record<string, any>,
  measurements?: Record<string, number>
) {
  logger.error('Tracking error:', error, properties, measurements);
  if (appInsights) {
    appInsights.trackException({
      exception: error,
      properties,
      measurements,
    });
  }
}

// Fonction pour tracker des métriques personnalisées
export function trackMetric(name: string, average: number, properties?: Record<string, any>) {
  if (appInsights) {
    appInsights.trackMetric({
      name,
      average,
      properties,
    });
  }
}

// Fonction pour tracker des vues de page
export function trackPageView(
  name?: string,
  url?: string,
  properties?: Record<string, any>,
  measurements?: Record<string, number>
) {
  if (appInsights) {
    appInsights.trackPageView({
      name,
      uri: url,
      properties,
      measurements,
    });
  }
}

// Fonction pour tracker des dépendances (appels API)
export function trackDependency(
  id: string,
  method: string,
  uri: string,
  success: boolean,
  duration: number,
  responseCode: number,
  properties?: Record<string, any>
) {
  if (appInsights) {
    appInsights.trackDependencyData({
      id,
      name: method,
      data: uri,
      success,
      duration,
      responseCode,
      properties,
    });
  }
}

// Fonction pour définir des propriétés utilisateur
export function setUserProperties(userId: string, accountId?: string) {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, accountId);
  }
}

// Fonction pour flush les données (utile avant la fermeture de l'app)
export function flush() {
  if (appInsights) {
    appInsights.flush();
  }
}

export { appInsights };
